import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Outlet } from 'react-router-dom';
import { useCallCenter } from '../providers/CallCenterProvider';
import Button from '../components/Button';
import Modal from '../components/Modal';
import TopBar from '../components/TopBar';
import AgentStatusBadge from '../components/AgentStatusBadge';
import ConfigStatus from '../components/ConfigStatus';
import { usePortalTarget } from '../util/PortalTarget';

const BADGE_PADDING_PX = 16;

export default function Root() {
    const { configuration, locationsLoading, locationsError } = useCallCenter();
    const [modalOpen, setModalOpen] = useState(false);
    const [topBarHeight, setTopBarHeight] = useState(0);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const portalTarget = usePortalTarget();

    // A callback ref instead of useRef+useEffect — this component returns
    // early (the loading/error state below) before the wrapper div ever
    // renders, so a mount-only effect would find a null ref and never retry
    // once the real content (and its ref) shows up on a later render. A
    // callback ref fires exactly when the DOM node actually appears.
    const topBarWrapperRef = useCallback((el: HTMLDivElement | null) => {
        resizeObserverRef.current?.disconnect();
        if (!el) {
            return;
        }
        resizeObserverRef.current = new ResizeObserver(([entry]) => {
            setTopBarHeight(entry.contentRect.height);
        });
        resizeObserverRef.current.observe(el);
    }, []);

    if (locationsLoading || locationsError) {
        return <ConfigStatus loading={locationsLoading} error={locationsError} />;
    }

    // TopBar itself adapts to whether a location's corp config has loaded yet
    // — it's rendered as soon as the corp's locations are known, showing just
    // the corp name and location picker until then.
    return (
        <div className='relative'>
            <div ref={topBarWrapperRef}>
                <TopBar setModalOpen={setModalOpen} />
            </div>

            {configuration && (
                <>
                    <AgentStatusBadge top={topBarHeight + BADGE_PADDING_PX} />

                    {portalTarget ? createPortal(<Outlet />, portalTarget) : <Outlet />}

                    <Modal show={modalOpen}>
                        <h3>How to use</h3>
                        <ul>
                            <li>Choose a location from the dropdown</li>
                            <li>Enter your agent name and comments</li>
                            <li>Click on Save</li>
                        </ul>

                        <p>
                            After saving, the details you entered will transfer through to
                            Sitelink when creating a reservation
                        </p>

                        <Button
                            onClick={() => setModalOpen(false)}
                            corporation={configuration}
                        >
                            Close
                        </Button>
                    </Modal>
                </>
            )}
        </div>
    );
}
